import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import type { Sites } from '@ps/platform-message-framework/common/Sites';
import type { Currency, FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import type { KBAQuestionType } from '@ps/web-core-store';
import type { DeepPartial } from 'utility-types';

import type { FieldsMap } from '@swf/components/StarsWizard/Machine';
import type { JumpDirection } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import { type FormMethod } from '@swf/core/enums';
import type { Error, ErrorCollection, FieldErrors } from '@swf/core/types/Response';

import type { ChallengeWindowSize } from '../utils/browserExtraInfo';

import type { CardType } from './cardType';
import type { LossLimitType, ResponsibleGamingLimitsPayload } from './responsibleGamingLimits';
import type { CashoutStatus, DepositStatus, RealMoneyStatus, TransactionStatus } from './status';
import type { TransactionGroup } from './transactionGroup';

export interface Field {
  editable?: boolean;
  name: string;
  errors?: FieldErrors;
  value?: string | number | null | boolean;
}

export type Fields = Field[];

export interface SecurityQuestion {
  id: number;
  question: string;
}

export type SecurityQuestions = SecurityQuestion[];

export interface RealMoneyInfo {
  license: LicenceId;
  site: Sites;
  brand: BrandTypes;
  realMoneyStatus: RealMoneyStatus;
  userInfoFields: Fields;
  errors: ErrorCollection;
  securityQuestions: SecurityQuestions;
  licenseMobileVerified?: boolean;
  showDepositLimits?: boolean;
}

export interface PaymentMethod {
  balanceCurrency?: Currency;
  cardInfoId?: number;
  cardType: CardType;
  i18nSystemName?: string;
  maskedCardNum?: string;
  preferred?: boolean;
  verified?: boolean;
  expYear?: number;
  expMonth?: number;
  daysLeft?: number;
  expired?: boolean;
}

export type PaymentMethods = PaymentMethod[];

export interface PaySystem extends PaymentMethod {
  systemName: string;
  once: number;
  transaction: number;
  month: number;
  week: number;
  day: number;
  fee: string;
  comment: string;
  maxDeposits: number;
  depositHours: number;
  limitInfo: string;
  supportFastDeposit: boolean;
  supportCardIO: boolean;
}

export type PaySystems = PaySystem[];

enum PaySystemLimitData {
  Day = 'day',
  Month = 'month',
  Once = 'once',
  Transaction = 'transaction',
  Week = 'week'
}

export enum CashierFlowType {
  FirstDeposit = 'firstDeposit',
  FirstManageCurrencies = 'firstManageCurrencies',
  FirstP2pTransfer = 'firstP2pTransfer',
  FirstPaymentMethods = 'firstPaymentMethods',
  FirstTransactionHistory = 'firstTransactionHistory',
  FirstWithdraw = 'firstWithdraw',
  WC2Deposit = 'wc2Deposit',
  WC2ManageCurrencies = 'wc2ManageCurrencies',
  WC2P2pTransfer = 'wc2P2pTransfer',
  WC2TransactionHistory = 'wc2TransactionHistory',
  WC2Withdraw = 'wc2Withdraw'
}

export type PaymentVerificationFlowType =
  | CashierFlowType.FirstDeposit
  | CashierFlowType.FirstWithdraw;

export enum GooglePayNativePaymentError {
  Failed = 'PAYMENT_FAILURE',
  Cancelled = 'PAYMENT_CANCELED',
  Timeout = 'PAYMENT_TIMEOUT'
}

export type MaxLimitData = Pick<
  PaySystem,
  | PaySystemLimitData.Day
  | PaySystemLimitData.Month
  | PaySystemLimitData.Transaction
  | PaySystemLimitData.Week
>;

export type MinLimitData = Pick<
  PaySystem,
  PaySystemLimitData.Once | PaySystemLimitData.Transaction
>;

export interface BonusInfo {
  bonusId: number;
  title: string;
  url: string;
  description: string;
  paySystems: CardType[];
  currencySpecifics: {
    [key in Currency]?: {
      minAmount: number;
      maxAmount: number;
    };
  };
}

export interface TicketInfo {
  ticketName: string;
  numOfTickets: number;
}

export interface PreCashinInfo {
  ticketSet: TicketInfo[];
  potentialBonusAmount: number;
  fppToExercise: number;
  maxPossibleCashInAmount: number;
  bonusId: number;
  bonusPercent: number;
  bonusMax: number;
  bonusName: string;
  bonusStart: number;
  bonusEnd: number;
  bonusUrl: string;
  outstandingCashout: number;
  releasePortion: number;
  fppReleasePortion: number;
  bonusFlags: number;
  maxNumberOfDepositsLeft: number;
  lastAllowedDepositTime: number;
  bonusCurrency: Currency;
  maxDaysForDepositLeft: number;
  maxNumberOfDeposits: number;
  balanceAmount: number;
  showProtectedFundsWarning: boolean;
  numOfTickets: number;
  bonusTicketName: string;
  ticketReleasePortion: number;
  maxNumTickets: number;
  amountReleasePortion: number;
  instantBonus: boolean;
  instantBonusInfo: InstantBonusInfo | null;
  pepCheck?: boolean;
  thirdPartyCheck?: boolean;
}

export interface InstantBonusInfo {
  instantBonusId: number;
  bonusAmountInBonusCurrency: number;
  bonusCurrency: Currency;
  bonusAmountInDepositCurrency: number;
  depositCurrency: Currency;
  cashPrice: number;
  cashPriceCurrency: Currency;
  productIds: number;
  pointsToClear: number;
  betsToClear: number;
  products: string[];
  splitWager?: boolean;
  gameMaxBetInAccountCurrency: number;
  urRedemptionLimit?: number;
}

export interface BankInfo {
  institution: string;
  branch: string;
  accountNumber: string;
  accountType: string;
}

export interface EcheckInfo {
  bankAttributes: BankInfo;
  ssnNumber: string;
  savedCardInfoId: number;
}

export interface EnvoyBankInfo {
  bankrefId: number;
  currency: Currency;
  name: string;
  city: string;
  address: string;
  swift: string;
  url: string;
  accountNum: string;
  iban: string;
  beneficiary: string;
  extraInfo: string;
  preferred: boolean;
}

export interface InovapayBankInfo {
  curr: Currency[];
  description: string;
  logoImageId: number;
  productId: string;
}

export interface OpenBankingBankInfo extends InovapayBankInfo {
  url: string;
}

export interface FeeInfo {
  paysysFeeApplies: boolean;
  gracePeriod: number;
  totalGraceDeposits: number;
  leftGraceDeposits: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  rate_x_100K: number;
  minFee: number;
  maxFee: number;
  alternativePaysys: CardType;
  flatFee: boolean;
}

export interface QuickLink {
  linkDescr: string;
  linkType: number;
  linkUrl: string;
}

export type QuickLinks = QuickLink[];

export type ApplePayMerchantCapability =
  | 'supports3DS'
  | 'supportsEMV'
  | 'supportsCredit'
  | 'supportsDebit';

export interface PaySystemInfo {
  accountId?: string;
  fraudnetSrcWebId?: string;
  hasAccount: boolean;
  minLoadLimit: number;
  maxLoadLimit: number;
  supportBillingAgreement?: boolean;
  supportedNetworks?: string[];
  merchantCapabilities?: ApplePayMerchantCapability[];
  googlePayMerchantPublicKey?: string;
  googleApiVersion?: string;
  googleAllowedCardType?: string[];
  googlePKIProtocolVersion?: string;
  googleMerchantName?: string;
  googlePayZeroExponentCurrencies?: string;
  googleAllowedCardSchemes?: string[];
  googleAllowedAuthMethods?: string;
  googleAcquisitionCountry?: string;
}

export interface SuggestedDepositAmountsForCurrency {
  buttons: number[];
  currency: Currency;
  freeEntryValue: number;
  freeEntryValueSet: boolean;
  selectedIndex: number;
}

export interface SuggestedDepositAmounts {
  suggestedDepositAmountsForCurrency: SuggestedDepositAmountsForCurrency[];
}

export interface VoucherAmount {
  currency: Currency;
  amount: number;
}

export type VoucherAmounts = VoucherAmount[];

export interface VoucherInfo {
  voucherAmounts: VoucherAmounts;
}

export interface PrepaidBalanceInfo {
  balanceAmount: number;
  maxBalance: number;
  currency: Currency;
}

export interface PsPayBank {
  accountType: string;
  bankAccount: string;
  bankCode: string;
  bankCurrency: Currency;
  bankId: number;
  bankName: string;
  branch: string;
  cashoutSetup: boolean;
  country: string;
  default: boolean;
  nicAlias: string;
  nicValue: string;
}

export interface Rate {
  curr: Currency;
  rate: number;
  invRate: number;
}

export type Rates = Rate[];

export interface CashoutOptionRate {
  allowed: boolean;
  curr: Currency;
  explanation: string;
  invRate: number;
  rate: number;
  text: string;
}

export interface RatesInfo {
  curr: Currency;
  rates: Rates;
}

export enum DepositLimitHitTypes {
  NetDepositLimit = 'NetDepositLimit',
  PaysystemLimit = 'PaysystemLimit',
  SelfImposedLimit = 'SelfImposedLimit',
  TempAccountLimit = 'TempAccountLimit',
  StaffLimit = 'StaffLimit',
  UkSpendBudgetLimit = 'UkSpendBudgetLimit',
  SpainDepositLimit = 'SpainDepositLimit'
}

export enum DaysLimitHitTypes {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  NotApplicable = 'NotApplicable'
}

export type UsedDaysLimitHitTypes =
  | DaysLimitHitTypes.Daily
  | DaysLimitHitTypes.Weekly
  | DaysLimitHitTypes.Monthly;

export interface PaySystemDetails {
  authenticationRequired: boolean;
  allowedSubsystems: (InovapayBankInfo | OpenBankingBankInfo)[];
  available: boolean;
  bonusCodes: string[];
  cardNumLen: number;
  dailyLimit: number;
  daysForClear: number;
  daysLimitHitType: UsedDaysLimitHitTypes;
  depositLimitHitType: DepositLimitHitTypes;
  enableBonusCode: boolean;
  info: PaySystemInfo;
  isNeedAgeVerify: boolean;
  monthlyLimit: number;
  msg: string;
  paysysFee: FeeInfo;
  quickLinks: QuickLinks;
  rates: RatesInfo;
  remainingLimit: number;
  selfLimitAmount: number;
  showFastDeposit: boolean;
  suggestedDepositAmounts: SuggestedDepositAmounts;
  supportFastDeposit: boolean;
  type: number;
  weeklyLimit: number;
  productId?: string;
  rmNokAllowed?: boolean;
}

export interface PaySystemDetailsResponse {
  asPaybankInfo: PsPayBank[] | null;
  echeckInfo: EcheckInfo | null;
  mobilePhone: string | null;
  paySystemDetails: PaySystemDetails;
  prepaidBalanceInfo: PrepaidBalanceInfo | null;
  savedCardInfoId: number | null;
  transId: number | null;
  voucherInfo: VoucherInfo | null;
}

export interface DepositPayload {
  amount: number;
  androidPackageName?: string;
  iosSchema?: string;
  applePayPaymentToken?: string;
  balanceCurrency: Currency;
  bankId?: number;
  bankInfo?: EnvoyBankInfo;
  accountNumber?: string;
  bonusAcknowledged?: boolean;
  bonusCode?: string;
  cumulativeCheck?: boolean;
  currency: Currency;
  dialingCode?: string;
  fraudnetMerSessionId?: string;
  googlePayCardInfo?: string;
  googlePayPaymentToken?: string;
  ignorePreviousDeclines: boolean;
  makeFastDeposit?: boolean;
  phoneNumber?: string;
  productId?: string;
  pepCheck?: boolean;
  rate: number;
  repeatedCashinDeclineOptions: string[];
  saveCardInfo?: boolean;
  savedCardInfoId?: number;
  thirdPartyCheck?: boolean;
  type: CardType;
  upsellId?: number;
  worldpay3DS2ReferenceId?: string;
  worldpay3DS2WindowSize?: ChallengeWindowSize;
}

export interface Balance {
  currency: Currency;
  chips: number;
  tMoney: number;
  balanceAvailable: number;
  balanceAllocated: number;
  balanceTotal: number;
  unclearedForCashout: number;
  availableForCashout: number;
  preferred: boolean;
  cashoutOnly: number;
  instantBonusAvailable: number;
  mFreeSpinsTotal: number;
  instantBonusInPlay: number;
  userRollsSurrenderAmount: number;
  permanent: boolean;
  created: boolean;
  instantBonusTotal: number;
  availableForSpending: number;
}

export interface WithdrawPayload {
  accountNumber?: string;
  amount: number;
  balanceAmount: number;
  balanceCurrency: Currency;
  bankAccount?: string;
  balances: Balance[];
  cumulativeCheck?: boolean;
  currency: Currency;
  pepCheck?: boolean;
  selectedBalance: Balance;
  sortCode?: string;
  thirdPartyCheck?: boolean;
  type: CardType;
  fields?: Record<string, string>;
}

export interface ManageCurrenciesPayload {
  sourceAvailableBalance: number;
  sourceAmount: number;
  sourceCurrency: Currency;
  targetAmount: number;
  targetCurrency: Currency;
}

export interface TransferPayload {
  amountFrom: number;
  currencyFrom: string;
  userComment: string;
  userTo: string;
}

export interface SensitiveDepositPayload extends DepositPayload {
  accountNumber: string;
  cvn?: string;
  expiryDate?: string;
}

export interface DepositContext {
  deposit: DepositPayload;
  previousDeposit?: DepositPayload | null;
}

export interface RedirectContext extends DeepPartial<DepositContext> {
  appSchemaUrl?: string;
  cardType?: CardType;
  challenge3DS2MD?: string;
  cmsHeaderAndGridLayout: boolean;
  redirectStatus: DepositStatus;
  returnUrl: string;
  securityKey: string;
  theme: string;
  transactionId: string;
  paySystemName?: string;
  maskedCardNum?: string;
  subSystemName?: string;
  tx_status?: TransactionStatus;
  plaidLinkToken?: string | null;
  bonusAmount?: string;
  transactionInfo?: TransactionInfo;
}

export interface Redirect {
  url: string;
  method: FormMethod;
  prms?: object & { plaidLinkToken?: string };
}

export interface RedirectWithContext extends Redirect {
  context: RedirectContext;
}

export interface DepositSuggestion {
  cardType: CardType;
  description?: string;
}

export type DepositSuggestions = DepositSuggestion[];

export interface ResultInfo {
  balanceAmount: number;
  balanceCurrency: Currency;
  procAmount: number;
  procCurrency: Currency;
}

export interface PrepaidVisaDepositInfo {
  loadAmount: number;
  visaCharge: number;
  depositAmount: number;
  prepaidVisaBalance: number;
  masterCardJustIssued: boolean;
  loadSucceeded: boolean;
  depositSucceeded: boolean;
  cardNumber: string;
  expiryDate: string;
}

export interface WorldPay3DS2Info {
  url: string;
  jwt: string;
  md: string;
  transId: string;
}

export interface XPay3DS2Info {
  jwt: string;
}

export type FDMS3DS2Info = string;

export interface BankWireResultInfo {
  paymentReference: string;
  accountHolder: string;
  swift: string;
  city: string;
  bankAcct: string;
  bankName: string;
  country: string;
  additionalRef: string;
  specialId: string;
  name: string;
}

export interface DepositResult {
  bonusId: number;
  depositSuggestions: DepositSuggestions | null;
  info: Error | null;
  message: Error;
  paySystemDetails: PaySystemDetails;
  plaidLinkToken: string | null;
  qrCodeData?: string;
  realMoneyInformation: RealMoneyInfo | null;
  redirectStatus?: DepositStatus;
  redirectUrl: Redirect;
  repeatedCashinDeclineOptions: string[] | null;
  status: DepositStatus;
  systemFee: number | null;
  transactionId: string;
  trhash: string;
  uniqueRefId?: string;
  unusedLossLimit: number | null;
  url: string;
  paymentRefId: string;
  expiryTime: string;
  entityId: string;
  resultInfo:
    | ResultInfo
    | PrepaidVisaDepositInfo
    | BankWireResultInfo
    | WorldPay3DS2Info
    | FDMS3DS2Info
    | XPay3DS2Info
    | null;
  processor: string | null;
  preCashinInfo: PreCashinInfo | null;
  wc2Paycallback?: string;
  challenge3DS2MD?: string;
  retry?: boolean;
}

export interface PrepaidBalanceInfo {
  balanceAmount: number;
  maxBalance: number;
  currency: Currency;
}

export enum CashoutMethodType {
  OverCredit = 'overCredit',
  Rollback = 'rollback',
  UnknownOverCredit = 'unknownOverCredit'
}

export interface CashoutOption {
  allowed: boolean;
  cardType: CardType;
  explanation: string;
  ibanOptional: boolean;
  paySystemName: string;
  notVerifiedAccount: boolean;
  prepaidBalanceInfo: PrepaidBalanceInfo | null;
  rates: CashoutOptionRate[];
  ref: string;
  t: number | null;
  text: string;
  minWithdrawalAmount: number;
  rawDataRef?: string;
}

export interface CashoutProcessTransactions {
  action?: number;
  amount?: number;
  fee?: number;
  ref?: string;
  type?: number;
}

export interface CashoutResult {
  message?: Error;
  realMoneyInformation?: RealMoneyInfo;
  ref?: string;
  requestedAmount?: number;
  status?: CashoutStatus;
  statusCode?: string;
  transactionId?: string;
  transactions?: CashoutProcessTransactions[];
}

export interface RollbackOption {
  amt: number;
  cardType: CardType;
  currency: Currency;
  notVerifiedAccount: boolean;
  paySystemName: string;
  processorAmount: number;
  rate: number;
  ref: string;
  rawDataRef?: string;
  institutionId?: string;
  institutionName?: string;
}

export type RollbackOverCreditOption = RollbackOption &
  Partial<CashoutOption> & {
    overCreditCurrency?: Currency;
    methodType?: CashoutMethodType;
  };

export interface PaymentMethodFilter {
  cardType: CardType;
  i18nName: string;
  ref: string;
}

export interface TransactionsFilterStatus {
  firstTransactionDate: Date;
  transactionGroups: TransactionGroup[];
  paymentMethodsFilter: PaymentMethodFilter[];
}

export interface TransactionHistoryFilters {
  paySystem?: PaymentMethodFilter[];
  dateGte?: string;
  dateLte?: string;
  transactionGroups?: TransactionGroup[];
}

export interface PaymentMethodFilters {
  paySystem?: PaymentMethodFilter[];
}

export interface DbtSignUpField {
  alpha: boolean;
  caption: string;
  extras: string;
  flags: number;
  freeField: boolean;
  hidden: boolean;
  iban: boolean;
  id: string;
  maxLength: number;
  minLength: number;
  numeric: boolean;
  optional: boolean;
  readOnly: boolean;
  regafiID: number;
  userParam: boolean;
  value: string;
  verify: boolean;
}

export interface DbtSignUpFields {
  currencies: string[];
  fields: DbtSignUpField[];
}

export interface TransactionHistoryOption {
  action: string;
  amount: number;
  balanceAmount: number;
  balanceCurrency: Currency;
  cardType: CardType;
  currency: Currency;
  date: string;
  deposit: boolean;
  descriptor: string;
  fee: number;
  paymentMethodTransactions?: TransactionHistoryOption[];
  paysystemNameI18n: string;
  ref: string;
  refTooltip: string;
  transactionType: TransactionType;
  transactionStatus: TransactionStatus;
  transId: string;
  relatedRecords?: TransactionHistoryOption[];
  conversionRate?: number;
  group?: TransactionGroup;
  setPage?: (page: number) => void;
  completedDate?: string;
}

export interface TransactionHistoryData extends Omit<TransactionHistoryOption, 'ref'> {
  absBalanceAmount: number;
  canBeCancelled: boolean;
  formattedRef: string;
  childTransactions?: TransactionHistoryData[];
  date: string;
}

export interface PendingCashoutOption {
  accountNumber: string;
  balanceAmount: number;
  balanceCurrency: Currency;
  canBeCancelled: boolean;
  paySystem: string;
  processorAmount: number;
  processorCurrency: Currency;
  started: number;
  state: string;
  transactionId: string;
  type: CardType;
}

export interface CancelledTransactions {
  code: number;
  description: string;
  success: boolean;
}

export interface CancelledWithdrawResult {
  status: string;
  transactions?: CancelledTransactions[];
  message?: string;
}

export interface PsPayBank {
  accountType: string;
  bankAccount: string;
  bankCode: string;
  bankCurrency: Currency;
  bankId: number;
  bankName: string;
  branch: string;
  cashoutSetup: boolean;
  country: string;
  default: boolean;
  nicAlias: string;
  nicValue: string;
}

export interface ProvinceCity {
  city: string;
  province: string;
}

export interface BirthCountry {
  countryCode: string;
  countryName: string;
}

export interface BirthPlace {
  provinceCities: ProvinceCity[];
  countries: BirthCountry[];
  forceCountryFromServer: boolean;
}

export interface SecurityQuestion {
  id: number;
  question: string;
}

export interface RealMoneyInformation {
  addressLookupAvailable?: boolean;
  license?: LicenceId;
  site?: Sites;
  brand?: BrandTypes;
  realMoneyStatus?: RealMoneyStatus;
  userInfoFields?: Field[];
  errors?: ErrorCollection;
  securityQuestions?: SecurityQuestion[];
  licenseMobileVerified?: boolean;
  showDepositLimits?: boolean;
}

interface BonusCodeDetails {
  code: string;
  description: string;
}

export enum ProgressBarSteps {
  AccountDetails = 'account-details',
  AboutYou = 'about-you',
  Deposit = 'deposit',
  Bonus = 'bonus'
}

export interface ConvertedAsset {
  assetType: number;
  amount: number;
  currency: string;
  convertedAmount: number;
  convRate: number;
}

export interface ConvertedAssets {
  amount: number;
  currency: string;
  convertedAssets: ConvertedAsset[];
}

export interface FirstWizardData {
  asPaybankInfo: PsPayBank[];
  availableSavedCards: OptionType[];
  balances: Balance[];
  bonusCodeDetails?: BonusCodeDetails[];
  bonusInfo?: BonusInfo;
  bonusReadySystems: CardType[];
  bonusStatus: BonusStatus;
  cancelledWithdrawResult: CancelledWithdrawResult;
  cashierFlow: CashierFlowType;
  cashoutOptions: CashoutOption[];
  cmsHeaderAndGridLayout?: boolean;
  convertRate: number;
  crmBonusCode: string;
  previousBonusCode?: string;
  deposit: DepositPayload;
  depositResult: DepositResult | null;
  depositResultContext?: RedirectContext;
  depositRestricted: boolean;
  depositRmfRequiredPageVisited: boolean;
  dbtSignUpFields: DbtSignUpFields;
  enabledBalances?: Balance[];
  enabledCurrencies?: Currency[];
  envoyBanks: EnvoyBankInfo[];
  features: { [key in FeatureFlag]?: boolean };
  fieldsMap: FieldsMap[];
  goJump?: { target: string; direction: JumpDirection };
  googlePayNativeResultError?: GooglePayNativeResultError;
  hasBalanceDiscrepancy: boolean;
  initialRatesList: Rates;
  initialSourceCurrency: Currency;
  initialTargetCurrency: Currency;
  italyBirthPlace: BirthPlace;
  kbaQuestions?: KBAQuestionType[];
  kbaAnswers?: { questionID: number; answer: string }[];
  limitSet: boolean;
  overCredit: number;
  paySystemDetails: PaySystemDetails;
  paySystems: PaySystems;
  paymentMethodTransactions: TransactionHistoryOption[];
  pendingCashouts: PendingCashoutOption[];
  preCashinInfo?: PreCashinInfo;
  previousDeposit: DepositPayload | null;
  processToPendingCashout?: boolean;
  productId?: string;
  progressBarStep?: ProgressBarSteps;
  previousSubmittedCurrency: Currency | undefined;
  realMoneyInformation: RealMoneyInformation | null;
  regularMethodSelected?: boolean;
  remainingCashoutAmount: number;
  taxAmount: number;
  kbaQuestionsPointer: {
    current: number;
    prev: number;
    next: number;
  };
  recentUsernames?: string[];
  regafiID?: number;
  rmfPassed: boolean;
  playerFundsProtection?: boolean;
  rmfTargetToJump?: string;
  rollback: number;
  rollbackOptions: RollbackOption[];
  unverifiedOptions: RollbackOption[];
  savedCardInfoId?: number;
  savedCards: SavedCards;
  selectedBalance: Balance;
  selectedOverCreditRecord?: RollbackOverCreditOption;
  signupPassed: boolean;
  transactionsFilterStatus: TransactionsFilterStatus;
  transferOutLimits: Record<string, PaySystem>;
  userInfoFields?: Field[];
  voucherInfo: VoucherInfo | null;
  withdrawResult: CashoutResult;
  preCashinData?: {
    cumulativeCheck?: boolean;
    pepCheck?: boolean;
    thirdPartyCheck?: boolean;
  };
  dbtFieldsData?: DbtFieldsData;
  mitIdResultContext?: { extAuthToken: string };
  lossLimits: LossLimitType[];
  depositLimits: DepositLimitResponse;
  regAction?: number;
  isContinueWithoutBonusSelected?: boolean;
  SSNConfirmationRequired?: boolean;
  convertedAssets: ConvertedAssets;
  pepHioAndThirdPartyCheckPassed?: boolean;
}

export interface FirstWizardValues {
  bonusCode?: string;
  city?: string;
  citizenship?: string;
  country?: string;
  deposit: DepositPayload;
  depositLimit?: ResponsibleGamingLimitsPayload;
  setDepositLimits: boolean;
  dob?: Date;
  emailVerificationCode?: string;
  firstName?: string;
  gender?: string;
  lastName?: string;
  secondLastName?: string;
  lossLimit?: ResponsibleGamingLimitsPayload;
  manageCurrencies: ManageCurrenciesPayload;
  mobilePhoneNumber?: number;
  intentToPlay?: number;
  paymentMethod: PaymentMethod;
  paymentMethodTransactions: TransactionHistoryOption[];
  paymentVerification: {
    menuSelected: PaymentVerificationTypes;
    files: File[];
  };
  pendingCashouts: {
    transactions: string[];
    password: string;
    menuSelected: 'pendingCashout' | 'deposit';
  };
  recentUsernames?: string[];
  state?: string;
  streetAddress?: string;
  sumSubVerification: {
    passed: boolean;
  };
  transfer: TransferPayload;
  userLimitsSelect: {
    skipped: boolean;
  };
  userId?: string;
  withdraw: WithdrawPayload;
  zip?: string;
  street?: string;
  buildingNumber?: string;
  docIssueDate?: string;
  occupationCompanyPhone?: string;
  occupationDialingCode?: string;
  playerIsNotPEP?: boolean;
  playerIsNot3Party?: boolean;
  pepHioSelection?: string;
  playerFundsProtection?: boolean;
}

export interface SpecificDataSingleSavedCard {
  bankId: string;
  bankName: string;
  clearingHouse?: string | null;
}

export interface SingleSavedCard {
  amount: number;
  cardInfoId: number;
  currencyValid: boolean;
  expMonth: number;
  expYear: number;
  fastDeposit: boolean;
  hostileBin: boolean;
  internalPaysystem: CardType;
  lastUse: number;
  maskedCardNum: string;
  paySystem: CardType;
  procCurrency: string;
  removable: boolean;
  specificData?: SpecificDataSingleSavedCard[];
  validationRequired: boolean;
  verified: boolean;
}

export interface SavedCard {
  savedCard: SingleSavedCard;
}

export type SavedCards = SavedCard[];

export interface OptionType {
  cardInfoId?: number;
  cardType: CardType;
  daysLeft?: number;
  hostileBin?: boolean;
  i18nSystemName?: string;
  maskedCardNum?: string;
  once: number;
  paySystem?: CardType;
  productId?: string;
  systemName?: PaySystem['systemName'];
  preferred?: boolean;
  verified?: boolean;
  expired?: boolean;
  expYear?: number;
  expMonth?: number;
}

export interface AvailablePaySystems {
  paySystems: PaySystems;
  applePayMerchantId: string | null;
}

export interface StarsWebGooglePay {
  supported: boolean;
  availableForPayment: boolean;
  // availabilityRequest is a JSON object (or JSON string) that contains a JSON description of an IsReadyToPayRequest
  // (see https://developers.google.com/android/reference/com/google/android/gms/wallet/IsReadyToPayRequest)
  checkAvailability(availabilityRequest: object): Promise<boolean>;
  // paymentData is a JSON object (or JSON string) that contains a JSON description of a PaymentDataRequest
  // (see https://developers.google.com/android/reference/com/google/android/gms/wallet/PaymentDataRequest).
  requestPayment(paymentData: object): void;
}

export interface GooglePayApiVersion {
  version: number;
  minor: number;
}

export interface GooglePayServerSettings {
  merchantPublicKey?: string;
  googlePayApiVersion: GooglePayApiVersion;
  environment?: string;
  allowedCardSchemes: string[];
  allowedAuthMethods: string;
  allowedCardType: string[];
  PKIProtocolVersion?: string;
  allowedPaymentMethodType: string;
  zeroExponentCurrencies?: string;
  merchantName?: string;
  merchantId?: string;
  countryCode?: string;
}

export interface GooglePayNativeResultError {
  status: DepositStatus;
}

export enum TransactionType {
  UNKNOWN = 'UNKNOWN',
  PURCHASE = 'PURCHASE',
  WITHDRAWAL = 'WITHDRAWAL',
  CREDIT_BACK = 'CREDIT_BACK',
  REFUND = 'REFUND',
  T_TICKET_PURCHASE = 'T_TICKET_PURCHASE',
  T_TICKET_SALE = 'T_TICKET_SALE', // sale of TSALE
  T_DEAL = 'T_DEAL', // TOURNDEAL
  RELEASED = 'RELEASED', // CASHTRANS_FLAG_RMOK_REMOVED_HOLD
  HELD = 'HELD', // DEBIT_FUNDS_RMOK_REMOVED_NAME
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  COMPENSATION = 'COMPENSATION', // admin or credit with err Info "EC_COMP"
  TRANSFER_IN = 'TRANSFER_IN', // PAYSYSTEM_AG_NAME //PYR-52150, PYR-61845
  TRANSFER_OUT = 'TRANSFER_OUT', // PAYSYSTEM_AG_NAME //PYR-52150, PYR-61845
  TRANSFER_OUT_CANCELLED = 'TRANSFER_OUT_CANCELLED', // CHQ_CANCEL Asia
  WITHDRAWAL_CANCELLED = 'WITHDRAWAL_CANCELLED', // CHQ_CANCEL others
  REWARD = 'REWARD', // bonus name in eLicenceBelgium
  REBATE = 'REBATE', // bonus name in LicenseRules::wordRebateRequired(trans.licenseId)) //PYR-30972
  BONUS_CLEARED = 'BONUS_CLEARED', // general bonus
  BONUS_PURCHASED = 'BONUS_PURCHASED', // instant bonus
  CORRECTION = 'CORRECTION', // by admin
  WITHDRAWAL_REFUND = 'WITHDRAWAL_REFUND', // admin or non-admin
  RETURN_CORRECTION = 'RETURN_CORRECTION', // admin
  ADMIN_CREDIT = 'ADMIN_CREDIT', // applied by admin with note
  ADMIN_DEBIT = 'ADMIN_DEBIT', // applied by admin with note
  PAYMENT = 'PAYMENT', // AFFILIATE_NAME
  CHARGE_BACK = 'CHARGE_BACK', // card=CHARGEBACK_NAME
  RETURN = 'RETURN', // returns for cards not CHARGEBACK_NAME
  REVERSED_RETURN = 'REVERSED_RETURN', // card= ELV REPAYMENT
  PAYSYSTEM_FEES = 'PAYSYSTEM_FEES', // card= PAYSYSTEM_FEE_NAME
  TAX = 'TAX', // card=PAYSYSTEM_TAX_NAME !isPurchase
  MIGRATION_FUNDS_IN = 'MIGRATION_FUNDS_IN', // es migration
  MIGRATION_FUNDS_OUT = 'MIGRATION_FUNDS_OUT', // es migration
  CURRENCY_CONVERSION = 'CURRENCY_CONVERSION',
  P2P_TRANSFER_OUT = 'P2P_TRANSFER_OUT',
  P2P_TRANSFER_IN = 'P2P_TRANSFER_IN'
}

export enum LimitTypes {
  Default = 0,
  NetDepositLimit = 2,
  SelfImposedLimit = 3,
  RegulatoryLimit = 5,
  SpendBudgetLimit = 7
}

export enum CashierLimitTypes {
  SelfImposedLimit = 'SELF_IMPOSED',
  RegulatoryLimit = 'REGULATORY'
}

export enum CashierLimitTimespan {
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY'
}

export enum PaymentVerificationTypes {
  verify = 'verify',
  verifyLater = 'verifyLater'
}

export enum DepositLimitStatus {
  NotSet = 'NOT_SET',
  Completed = 'COMPLETED',
  InProgress = 'IN_PROGRESS',
  SelfExcluded = 'SELF_EXCLUDED'
}

type Timespan = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface DepositLimit {
  totalAmount: number;
  remainingAmount: number;
  limitType: string;
  limitStatus: DepositLimitStatus;
  nextDepositDate?: number;
  progress: number;
  timespan: Timespan;
}

export interface DepositLimitsData {
  selfImposed: DepositLimit;
  regulatory: DepositLimit;
  netDeposit: DepositLimit;
}

export interface DepositLimitData {
  enforcedUntil: string | null;
  limit: number;
  limitMax: number;
  limitMin: number;
  limitThreshold: number;
  regulatoryLimit: number;
}

export interface DepositLimitResponse {
  daily: DepositLimitData;
  weekly: DepositLimitData;
  monthly: DepositLimitData;
  selfLimitCurrency: string;
  rateToUsd: number;
}

interface AvailableBonusDetails {
  url: string;
  name: string;
  description: string;
}

interface TaxInfo {
  taxAmount: number;
  taxableCashout: number;
  totalBalance: number;
  unusedBalance: number;
  indiaTaxFreeCashout: number;
  indiaTaxRate_x1M: number;
}

interface PanInfo {
  panStored: boolean;
  panVerified: boolean;
}

interface TempAccountCashoutLimitAmount {
  amt: number;
  currency: string;
}

export interface BonusStatus {
  availBonusDetails: AvailableBonusDetails[] | null;
}

export interface CashoutInfoResponse {
  bonusStatus: BonusStatus;
  realMoneyInformation: string;
  authVerified: boolean;
  accValidation: { status: number };
  hasCashoutHistory: boolean;
  info: {
    pan?: PanInfo;
    tax?: TaxInfo;
    docUpdateRequired?: boolean;
  };
  operatorRate?: {
    curr?: string;
    rate?: number;
    invRate?: number;
  };
  tempAccountCashoutLimitStatus: {
    cashoutAvailable: TempAccountCashoutLimitAmount;
    cashoutLimit: TempAccountCashoutLimitAmount;
  };
}

export interface HelpSection {
  title: TranslationTag;
  body: React.ReactNode;
}

export interface DbtFieldsData {
  currencies: string[];
  fields: DbtField[];
}

interface DbtField {
  id: string;
  caption: string;
  value: string;
  minLength: number;
  maxLength: number;
  flags: number;
  extras: string;
  hidden: boolean;
  readOnly: boolean;
  numeric: boolean;
  alpha: boolean;
  iban: boolean;
  verify: boolean;
  userParam: boolean;
  freeField: boolean;
  optional: boolean;
}

export enum CesFeedbackFormFlowType {
  Deposit = 'DEPOSIT',
  ConvertCurrency = 'CONVERT_CURRENCY',
  P2pTransfer = 'P2P_TRANSFER',
  TransactionHistory = 'TRANSACTION_HISTORY',
  Withdraw = 'WITHDRAW'
}

export enum DocumentTypeCatPortugal {
  NATIONAL_CIVIL_ID = 1,
  PASSPORT = 2,
  DRIVERS_LICENSE = 3,
  OTHER = 4
}

interface TempAccountDepositLimit {
  amt: number;
  currency: string;
  amtAsInt: number;
}

interface TempAccountGracePeriod {
  gracePeriodConfig: boolean;
  gracePeriodDays: number;
}

export interface TempAccountDepositStatus {
  depositLimit: TempAccountDepositLimit;
  availableDepositAmount: TempAccountDepositLimit;
  gracePeriod: TempAccountGracePeriod;
}

export interface TransactionInfo {
  transId: string;
  status: string;
  paysystemByte: number;
  gateway: string;
  procAmount: number;
  procCurrency: string;
  balanceAmount: number;
  balanceCurrency: string;
  paysystemNameI18N: string;
  czUserPermanent: boolean;
  paymentVerified: boolean;
  cardVerificationIsRequired: boolean;
  suspendRM: boolean;
}
